import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

function QueryCard({ q }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-start justify-between gap-4 mb-3">
        <p className="text-gray-800 font-medium text-sm leading-relaxed">{q.query_text}</p>
        <span
          className={`shrink-0 px-2.5 py-1 text-xs font-semibold rounded-full ${
            q.is_resolved
              ? 'bg-green-100 text-green-700'
              : 'bg-yellow-100 text-yellow-700'
          }`}
        >
          {q.is_resolved ? 'Resolved' : 'Pending'}
        </span>
      </div>

      {q.admin_response && (
        <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3.5">
          <p className="text-xs font-semibold text-indigo-600 mb-1">Team Response</p>
          <p className="text-gray-700 text-sm leading-relaxed">{q.admin_response}</p>
        </div>
      )}

      <p className="text-gray-400 text-xs mt-3">
        Submitted on {new Date(q.created_at).toLocaleDateString('en-US', {
          year: 'numeric', month: 'long', day: 'numeric',
        })}
      </p>
    </div>
  );
}

export default function AskUsPage() {
  const { user }                    = useAuth();
  const [queryText, setQueryText]   = useState('');
  const [queries, setQueries]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [flash, setFlash]           = useState({ type: '', msg: '' });

  const loadQueries = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('user_queries')
      .select('*')
      .eq('user_id', user?.id)
      .order('created_at', { ascending: false });
    setQueries(data ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => { if (user) loadQueries(); }, [loadQueries, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!queryText.trim()) return;
    setSubmitting(true);
    setFlash({ type: '', msg: '' });
    const { error } = await supabase
      .from('user_queries')
      .insert({ user_id: user.id, query_text: queryText.trim() });
    if (error) {
      setFlash({ type: 'error', msg: 'Submission failed. Please try again.' });
    } else {
      setFlash({ type: 'success', msg: "Your question has been submitted. We'll respond shortly!" });
      setQueryText('');
      loadQueries();
    }
    setSubmitting(false);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8">

      {/* Submission form */}
      <section className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-0.5">Ask a Question</h3>
        <p className="text-gray-500 text-sm mb-5">Our team personally reads and responds to every query.</p>

        {flash.msg && (
          <div
            className={`rounded-lg px-4 py-3 mb-4 text-sm border ${
              flash.type === 'success'
                ? 'bg-green-50 text-green-700 border-green-200'
                : 'bg-red-50 text-red-700 border-red-200'
            }`}
          >
            {flash.msg}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <textarea
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
            rows={4}
            placeholder="What would you like to know? Ask anything about the platform or its content…"
            className="w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none transition text-sm"
          />
          <button
            type="submit"
            disabled={submitting || !queryText.trim()}
            className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            {submitting ? 'Submitting…' : 'Submit Question'}
          </button>
        </form>
      </section>

      {/* Query history */}
      <section>
        <h3 className="text-base font-bold text-gray-900 mb-4">Your Questions</h3>
        {loading ? (
          <div className="text-center py-12 text-gray-400 text-sm">Loading your queries…</div>
        ) : queries.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <p className="text-4xl mb-3">💬</p>
            <p className="font-medium">No questions yet</p>
            <p className="text-sm mt-1">Submit your first question above.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {queries.map((q) => <QueryCard key={q.id} q={q} />)}
          </div>
        )}
      </section>

    </div>
  );
}
